import React from "react";
import {
  PRODUCT_IMAGE_FRAME_CLASS,
  PRODUCT_IMAGE_FRAME_SQUARE_CLASS,
  PRODUCT_IMAGE_MEDIA_CLASS,
  isProductImageSquare,
} from "../../Utils/productImage/productImageDisplay";
import { preserveBrokenImageBox } from "../../Utils/imageUtils";
import "./ProductBankImageFrame.css";

export type ProductBankImageFrameProps = {
  src: string;
  alt?: string;
  className?: string;
  imgClassName?: string;
  /** When true, img stretches to 100% of the frame (still object-fit: contain). */
  fill?: boolean;
  /** Media shape: "sq" / "square" / true → 143×143; otherwise 143×250. */
  shape?: string | boolean | null;
  /** Alias for shape === "sq". */
  square?: boolean;
  style?: React.CSSProperties;
  imgStyle?: React.CSSProperties;
  children?: React.ReactNode;
} & Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "alt" | "className" | "style" | "children"
>;

/**
 * Canonical product image presentation:
 * Rectangle 143×250 or Square 143×143, centered, contain (never cropped),
 * white background, 1px border.
 */
const ProductBankImageFrame = ({
  src,
  alt = "Product",
  className = "",
  imgClassName = "",
  fill = false,
  shape,
  square = false,
  style,
  imgStyle,
  children,
  onError,
  ...imgProps
}: ProductBankImageFrameProps) => {
  if (!src) return null;

  const isSquare = square || isProductImageSquare(shape, className);
  const frameClasses = [
    PRODUCT_IMAGE_FRAME_CLASS,
    isSquare ? PRODUCT_IMAGE_FRAME_SQUARE_CLASS : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const mediaClasses = [
    PRODUCT_IMAGE_MEDIA_CLASS,
    fill ? `${PRODUCT_IMAGE_MEDIA_CLASS}--fill` : "",
    imgClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={frameClasses} style={style}>
      <img
        src={src}
        alt={alt}
        className={mediaClasses}
        decoding="async"
        style={imgStyle}
        onError={onError || preserveBrokenImageBox}
        {...imgProps}
      />
      {children}
    </div>
  );
};

export default ProductBankImageFrame;
